/**
 * Product navigation — roadmap 10.1, §1.4.
 *
 * The primary navigation: *what am I browsing*. The role axis — *who am I
 * acting as* — is in `AccountMenu`, and the two must not merge.
 *
 * Each product owns a dropdown. `DropdownMenuRoot` is Radix underneath, which
 * is what supplies the keyboard contract roadmap 10.3 asks for: arrows move
 * within the menu, `Escape` closes it and returns focus to the trigger, and the
 * whole thing mirrors under `dir="rtl"` without a physical property anywhere.
 * Writing this by hand would mean re-implementing all of that, badly.
 */
import { ChevronDown } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router'

import { activeProduct, visibleItemsFor, type ProductDefinition } from '@/app/products'
import { useSession } from '@/shared/auth/useSession'
import {
  Badge,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuRoot,
  DropdownMenuTrigger,
} from '@/shared/ui'
import { cn } from '@/shared/ui/cn'

/**
 * A product in the bar — the brand reference's quiet header (Task 4, 1 October
 * 2026): plain 14px text, no icons, no chips. Inactive items are ink and turn
 * wine on hover, as the reference's `nav a:hover` does.
 *
 * ⚠️ **The active product is wine text *and* a 2px wine rule under it** — not
 * the colour alone. Ink and wine are both dark, and a colour shift between two
 * dark text colours is not a state a sighted reader can rely on (WCAG 1.4.1);
 * the rule is the part that does the work, and as an indicator it owes 3:1
 * against the bar (1.4.11): `brand-fg` on the sunken ground measures 9.23:1
 * light and 8.69:1 dark. (Before Task 4 this was a white chip with an inset
 * wine bar; the chip went with the reference, the bar stayed as the rule.)
 * `aria-current="page"` carries the same state to assistive tech, and
 * `shell.test.tsx` asserts the active and inactive classes differ.
 *
 * The rule hangs 6px under the text (`after:`, absolutely placed), so the
 * active item is exactly as tall as its neighbours and nothing shifts when the
 * route changes.
 */
function productClass(isActive: boolean) {
  return cn(
    'relative inline-flex h-9 items-center gap-1 rounded-[3px] text-sm whitespace-nowrap transition-colors',
    "after:absolute after:inset-x-0 after:-bottom-1 after:h-0.5 after:rounded-full after:content-['']",
    isActive
      ? 'text-brand-fg after:bg-brand-fg'
      : 'text-fg after:bg-transparent hover:text-brand-fg',
  )
}

/**
 * A badged product (Education while its flag is off) reads as one muted line —
 * "Education · Coming soon" — as the reference's `.soon` span does, with the
 * badge's words kept (rule 4: never colour alone) and the pill gone. The dot is
 * decoration and hidden from assistive tech, so the name stays "Education
 * Coming soon".
 */
function ProductLabel({ product }: { product: ProductDefinition }) {
  const { t } = useTranslation('common')
  if (!product.badgeKey) return <>{t(product.labelKey)}</>
  return (
    <span className="text-fg-muted">
      {t(product.labelKey)} <span aria-hidden="true">·</span> {t(product.badgeKey)}
    </span>
  )
}

export function ProductNav({ products }: { products: ProductDefinition[] }) {
  const { t } = useTranslation('common')
  const session = useSession()
  const location = useLocation()
  const navigate = useNavigate()

  const active = activeProduct(products, location.pathname)

  return (
    <nav aria-label={t('nav.products')}>
      {/* The reference's 32px between items (16px under 900px). */}
      <ul className="flex items-center gap-5 lg:gap-8">
        {products.map((product) => {
          const items = visibleItemsFor(product, session.hasRole)
          const isActive = active?.id === product.id

          // ⚠️ **A one-item product is a link, not a menu.** `productsFor`
          // sets `directTo` when a viewer's visible items collapse to one —
          // which for a platform admin is the Events product, holding only
          // "Organize events". Making them open a dropdown to reach a single
          // destination is friction that reads as an unfinished menu.
          if (product.directTo) {
            return (
              <li key={product.id}>
                <button
                  type="button"
                  aria-current={isActive ? 'page' : undefined}
                  className={productClass(isActive)}
                  onClick={() => void navigate(product.directTo!)}
                >
                  <ProductLabel product={product} />
                </button>
              </li>
            )
          }

          return (
            <li key={product.id}>
              <DropdownMenuRoot>
                <DropdownMenuTrigger asChild>
                  <button
                    type="button"
                    // `aria-current` rather than colour alone: the active
                    // product must be announced, not only seen.
                    aria-current={isActive ? 'page' : undefined}
                    className={productClass(isActive)}
                  >
                    <ProductLabel product={product} />
                    {/* The one mark a menu needs: it says "this opens". */}
                    <ChevronDown aria-hidden="true" className="size-3.5 opacity-60" />
                  </button>
                </DropdownMenuTrigger>

                <DropdownMenuContent>
                  {items.map((item) => (
                    <DropdownMenuItem
                      key={item.id}
                      // `?? false`, not the bare optional: the project runs
                      // `exactOptionalPropertyTypes`, so passing `undefined`
                      // to a required-when-present prop is an error.
                      disabled={item.disabled ?? false}
                      onSelect={() => {
                        if (item.disabled) return
                        void navigate(item.to)
                      }}
                    >
                      <span className="flex-1">{t(item.labelKey)}</span>
                      {/* A disabled item says why it is disabled. Greying it
                          out alone reads as a bug in the menu rather than a
                          part of the product that has not shipped. */}
                      {item.disabled ? (
                        <span className="text-xs text-fg-subtle">{t('product.soon')}</span>
                      ) : null}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenuRoot>
            </li>
          )
        })}
      </ul>
    </nav>
  )
}

/**
 * The same products as a flat list, for the mobile drawer.
 *
 * Four dropdowns do not fit a phone (roadmap 10.3), and nesting menus inside a
 * sheet is a keyboard trap. Sections with headings instead: everything is
 * visible at once and reachable by tabbing.
 */
export function ProductNavVertical({ products }: { products: ProductDefinition[] }) {
  const { t } = useTranslation('common')
  const session = useSession()
  const navigate = useNavigate()

  return (
    <nav aria-label={t('nav.products')} className="space-y-5">
      {/* ⚠️ **`directTo` is deliberately not honoured here.** The desktop bar
          collapses a one-item product into a link because a dropdown holding
          one destination is friction. This drawer already renders every item
          as a flat row under its product heading, so a one-item product is
          *already* one tap — collapsing it further would only remove the
          heading that says which product the row belongs to. */}
      {products.map((product) => {
        const items = visibleItemsFor(product, session.hasRole)
        const Icon = product.icon

        return (
          <section key={product.id} aria-labelledby={`product-${product.id}`}>
            <h2
              id={`product-${product.id}`}
              className="mb-1 flex items-center gap-2 px-3 text-xs font-medium tracking-caps text-fg-subtle"
            >
              <Icon aria-hidden="true" className="size-3.5" />
              {t(product.labelKey)}
              {product.badgeKey ? (
                <Badge tone="brand" className="ms-1 normal-case">
                  {t(product.badgeKey)}
                </Badge>
              ) : null}
            </h2>
            <ul>
              {items.map((item) => (
                <li key={item.id}>
                  <button
                    type="button"
                    disabled={item.disabled ?? false}
                    onClick={() => void navigate(item.to)}
                    // `text-start`, not `text-left` — physical properties fail
                    // lint and would misalign in Arabic.
                    className={cn(
                      'flex w-full items-center gap-2 rounded-md px-3 py-3 text-start text-sm font-medium transition-colors',
                      item.disabled
                        ? 'cursor-not-allowed text-fg-subtle'
                        : 'text-fg-muted hover:bg-hover hover:text-fg',
                    )}
                  >
                    <span className="flex-1">{t(item.labelKey)}</span>
                    {item.disabled ? (
                      <span className="text-xs text-fg-subtle">{t('product.soon')}</span>
                    ) : null}
                  </button>
                </li>
              ))}
            </ul>
          </section>
        )
      })}
    </nav>
  )
}
